import React from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DailyConsumption } from "../types";
import { CRITERIA_COLORS } from "../../insights/utils/chartData";
import { EmptyState } from "../../../ui/EmptyState";
import { useChartTheme } from "../../theme";
import type {
  DailyChartRow,
  DailyCoreBreakdown} from "../utils/transformDailyConsumption";
import {
  transformDailyConsumption,
} from "../utils/transformDailyConsumption";
import { formatMonthLong } from "../utils/months";

interface DailyCreditsChartProps {
  selectedMonth: string;
  dailyConsumption: DailyConsumption[];
  cores: string[];
}

const intFmt = (n: number) => n.toLocaleString();
const creditsFmt = (n: number) =>
  n.toLocaleString(undefined, { maximumFractionDigits: 4 });

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
/** Format an ISO `YYYY-MM-DD` as e.g. "Jun 1" (parsed manually to avoid TZ shifts). */
const shortDate = (iso: string): string => {
  const [, m, d] = iso.split("-").map(Number);
  return m && d ? `${MONTHS_SHORT[m - 1]} ${d}` : iso;
};

/** Stacked vertical bar chart, one bar per day of the selected month, stacked
 * by model_core. Hover shows per-core requests/tokens/credits for the day.
 *
 * The X-axis always spans the full calendar month (current and past months
 * alike) so bar width stays constant regardless of how far into the month we
 * are — future days of the current month render as empty slots rather than
 * being dropped from the domain (design doc 013 §1c, revised for issue #1215). */
export function DailyCreditsChart({
  selectedMonth,
  dailyConsumption,
  cores,
}: DailyCreditsChartProps) {
  const chart = useChartTheme();
  const data = transformDailyConsumption(dailyConsumption, selectedMonth, cores);
  const hasAny = dailyConsumption.length > 0;

  if (!hasAny) {
    return (
      <EmptyState
        message="No usage in this month"
        description={`No evaluation requests recorded for ${formatMonthLong(
          selectedMonth,
        )}.`}
      />
    );
  }

  return (
    <div className="w-full h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 10, right: 16, left: 8, bottom: 0 }}
          maxBarSize={48}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
          <XAxis
            dataKey="date"
            tickFormatter={shortDate}
            minTickGap={24}
            tick={{ fontSize: 12, fill: chart.axis }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: chart.axis }}
            label={{
              value: "Credits",
              angle: -90,
              position: "insideLeft",
              fontSize: 12,
              fill: chart.axis,
            }}
          />
          <Tooltip content={<DailyTooltip />} cursor={{ fill: chart.cursor }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {cores.map((core, i) => (
            <Bar
              key={core}
              dataKey={core}
              stackId="credits"
              fill={CRITERIA_COLORS[i % CRITERIA_COLORS.length]}
              name={core}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload: DailyChartRow }>;
  label?: string;
}

function DailyTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const byCore: DailyCoreBreakdown = row._byCore;
  const cores = Object.keys(byCore);
  if (cores.length === 0) {
    return (
      <div className="bg-popover border border-border rounded-md shadow-md p-3 text-popover-foreground">
        <p className="font-medium text-sm">{shortDate(row.date)}</p>
        <p className="text-xs text-muted-foreground mt-1">No usage</p>
      </div>
    );
  }
  const totalCredits = cores.reduce(
    (acc, c) => acc + byCore[c].credits,
    0,
  );
  return (
    <div className="bg-popover border border-border rounded-md shadow-md p-3 min-w-[14rem] text-popover-foreground">
      <p className="font-medium text-sm mb-2">{shortDate(row.date)}</p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-muted-foreground">
            <th className="text-left font-medium pr-2">Model</th>
            <th className="text-right font-medium pr-2">Reqs</th>
            <th className="text-right font-medium pr-2">Tokens</th>
            <th className="text-right font-medium">Credits</th>
          </tr>
        </thead>
        <tbody>
          {cores.map((c) => (
            <tr key={c}>
              <td className="font-mono pr-2 truncate max-w-[10rem]">{c}</td>
              <td className="text-right font-mono pr-2">
                {intFmt(byCore[c].requests)}
              </td>
              <td className="text-right font-mono pr-2">
                {intFmt(byCore[c].tokens)}
              </td>
              <td className="text-right font-mono">
                {creditsFmt(byCore[c].credits)}
              </td>
            </tr>
          ))}
          <tr className="border-t border-border">
            <td className="font-semibold pt-1">Total</td>
            <td colSpan={2}></td>
            <td className="text-right font-mono font-semibold pt-1">
              {creditsFmt(totalCredits)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
